import React from 'react';
import { useWebSocket } from '../context/WebSocketContext';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Circle, Lightning, WifiHigh, WifiSlash } from '@phosphor-icons/react';

const RealTimeIndicator = ({ showLabel = true, size = 'default' }) => {
  const { isConnected, lastUpdate, closedReason } = useWebSocket();

  // When the socket was refused for auth / no-elevation reasons the
  // "Offline" indicator is misleading — the caller isn't offline, they
  // just haven't earned live updates for the current agency. Suppress
  // the indicator entirely; the AgencySwitcher / PlatformElevation
  // banner already surface the real CTA.
  if (!isConnected && closedReason) {
    return null;
  }

  const formatLastUpdate = () => {
    if (!lastUpdate) return 'Never';
    const now = new Date();
    const diff = Math.floor((now - lastUpdate) / 1000);
    
    if (diff < 5) return 'Just now';
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  const sizeClasses = {
    small: 'text-xs gap-1',
    default: 'text-sm gap-1.5',
    large: 'text-base gap-2',
  };

  const iconSize = size === 'small' ? 12 : size === 'large' ? 18 : 14;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`flex items-center ${sizeClasses[size]} cursor-default`}
            data-testid="realtime-indicator"
          >
            {isConnected ? (
              <>
                <div className="relative">
                  <Circle
                    size={iconSize}
                    weight="fill"
                    className="text-green-500"
                  />
                  <Circle
                    size={iconSize}
                    weight="fill"
                    className="text-green-500 absolute top-0 left-0 animate-ping opacity-75"
                  />
                </div>
                {showLabel && (
                  <span className="text-green-600 font-medium">Live</span>
                )}
              </>
            ) : (
              <>
                <WifiSlash size={iconSize} className="text-slate-400" />
                {showLabel && (
                  <span className="text-slate-500">Offline</span>
                )}
              </>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <div className="text-xs">
            <p className="font-medium">
              {isConnected ? 'Real-time updates active' : 'Reconnecting...'}
            </p>
            <p className="text-slate-400">Last update: {formatLastUpdate()}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const LiveDataBadge = () => {
  const { isConnected } = useWebSocket();

  if (!isConnected) return null;

  return (
    <Badge
      variant="outline"
      className="bg-green-50 border-green-200 text-green-700 gap-1 animate-pulse"
      data-testid="live-data-badge"
    >
      <Lightning size={12} weight="fill" />
      Live Data
    </Badge>
  );
};

export default RealTimeIndicator;
